import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { ProductDisplay, Grade } from '../models';
import GradeDisplay from './GradeDisplay';

// Dit is het kaartje dat je ziet in de lijsten.
// Het toont de foto, naam, merk en die belangrijke eco-score.
interface ProductKaartProps {
  // Het product dat we moeten laten zien
  product: ProductDisplay;
  // Wat er moet gebeuren als je erop klikt
  onPress: () => void;
  // Willen we de favorieten knop zien?
  showFavoriteButton?: boolean;
  // Is dit ding al favoriet?
  isFavorite?: boolean;
  // Functie voor als je op het hartje drukt
  onFavoriteToggle?: () => void;
}

// React.memo is slim: hij update dit component alleen als de props veranderen.
// Scheelt weer rekenkracht!
const ProductKaart: React.FC<ProductKaartProps> = React.memo(({
  product,
  onPress,
  showFavoriteButton = false,
  isFavorite = false,
  onFavoriteToggle,
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = React.useState(false);

  // Reset error state when product changes
  React.useEffect(() => {
    setImageError(false);
  }, [product.id, product.imageUrl]);

  return (
    <TouchableOpacity style={stijlen.kaart} onPress={onPress}>
      <View style={stijlen.plaatjeBak}>
        {product.imageUrl && !imageError ? (
          <Image 
            source={{ uri: product.imageUrl }} 
            style={stijlen.plaatje} 
            onError={() => setImageError(true)}
          />
        ) : (
          // Als er geen plaatje is (of hij is stuk), laten we een mooie placeholder zien
          <View style={stijlen.tempPlaatje}>
            <Text style={stijlen.tempPlaatjeTekst}>🌊</Text>
          </View>
        )}
      </View>

      <View style={stijlen.inhoud}>
        <View style={stijlen.tekstBak}>
          <Text style={stijlen.productNaam} numberOfLines={2}>
            {product.name}
          </Text>
          <Text style={stijlen.merkNaam} numberOfLines={1}>
            {product.brand}
          </Text>
        </View>

        <View style={stijlen.voet}>
          {/* Hier laten we die mooie A/B/C/D/E score zien */}
          <GradeDisplay grade={product.grade} size="small" />

          {showFavoriteButton && onFavoriteToggle && (
            <TouchableOpacity onPress={onFavoriteToggle} style={stijlen.favKnop}>
              <Text style={stijlen.favIcoon}>{isFavorite ? '❤️' : '🤍'}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
});

const stijlen = StyleSheet.create({
  kaart: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
  },
  plaatjeBak: {
    width: '100%',
    height: 150,
    backgroundColor: '#F5F5F5',
  },
  plaatje: {
    width: '100%',
    height: '100%',
  },
  tempPlaatje: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E8F4F8',
  },
  tempPlaatjeTekst: {
    fontSize: 48,
  },
  inhoud: {
    padding: 16,
  },
  tekstBak: {
    marginBottom: 12,
  },
  productNaam: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  merkNaam: {
    fontSize: 14,
    color: '#666',
  },
  voet: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  favKnop: {
    padding: 8,
  },
  favIcoon: {
    fontSize: 24,
  },
});

ProductKaart.displayName = 'ProductKaart';

export default ProductKaart;
